/**
 * AI Model Configuration Center hooks (Issue #187).
 *
 * 红线：
 * - 必须通过 `@jinzhuan/api-client` typed client 调用后端；禁止手写 fetch / axios /
 *   纯前端假状态。
 * - 凭证密钥明文绝不能出现在响应、本地存储、组件 state 中。后端只返回 alias / hint。
 * - 模型版本字符串接受任意值（如 gpt-5.5、claude-sonnet-4.5）；前端不应硬编码白名单。
 * - 价格 / 成本字段使用 Decimal 字符串展示（按字符串原样渲染），禁止 parseFloat 计算总额。
 */
import { useCallback, useEffect, useState } from 'react';
import { createApiClient } from '@/lib/adminApiClient';
import type {
  CreateLlmCredentialRequest,
  CreateLlmProviderRequest,
  LlmCallAuditAggregateDto,
  LlmCallAuditDto,
  LlmCredentialDto,
  LlmModelDto,
  LlmProviderDto,
  LlmProviderSyncRunDto,
  LlmQuotaDto,
  RecordLlmCallAuditRequest,
  TriggerProviderSyncResponse,
  UpdateLlmProviderRequest,
  UpsertLlmModelRequest,
  UpsertLlmQuotaRequest,
} from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

export type {
  CreateLlmCredentialRequest,
  CreateLlmProviderRequest,
  LlmCallAuditAggregateDto,
  LlmCallAuditDto,
  LlmCredentialDto,
  LlmModelDto,
  LlmProviderDto,
  LlmProviderSyncRunDto,
  LlmQuotaDto,
  RecordLlmCallAuditRequest,
  TriggerProviderSyncResponse,
  UpdateLlmProviderRequest,
  UpsertLlmModelRequest,
  UpsertLlmQuotaRequest,
};

export interface AsyncState<T> {
  readonly data: T | null;
  readonly loading: boolean;
  readonly error: string | null;
}

function pickErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  if (err && typeof err === 'object') {
    const maybeMessage = (err as { message?: string }).message;
    if (typeof maybeMessage === 'string') return maybeMessage;
  }
  return fallback;
}

// ----- Providers -----------------------------------------------------------

export function useLlmProviders(): AsyncState<readonly LlmProviderDto[]> & {
  refresh: () => Promise<void>;
  createProvider: (input: CreateLlmProviderRequest) => Promise<LlmProviderDto | null>;
  updateProvider: (
    id: string,
    input: UpdateLlmProviderRequest,
  ) => Promise<LlmProviderDto | null>;
  deleteProvider: (id: string) => Promise<boolean>;
} {
  const [state, setState] = useState<AsyncState<readonly LlmProviderDto[]>>({
    data: null,
    loading: false,
    error: null,
  });

  const refresh = useCallback(async () => {
    setState({ data: null, loading: true, error: null });
    try {
      const { data, error } = await apiClient.GET('/api/v1/ai-model-center/providers', {});
      if (error) {
        setState({ data: null, loading: false, error: pickErrorMessage(error, '加载平台失败') });
        return;
      }
      setState({ data: (data ?? []) as readonly LlmProviderDto[], loading: false, error: null });
    } catch (err) {
      setState({ data: null, loading: false, error: pickErrorMessage(err, '加载平台失败') });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const createProvider = useCallback(
    async (input: CreateLlmProviderRequest): Promise<LlmProviderDto | null> => {
      try {
        const { data, error } = await apiClient.POST('/api/v1/ai-model-center/providers', {
          body: input,
        });
        if (error) {
          throw new Error(pickErrorMessage(error, '创建平台失败'));
        }
        await refresh();
        return (data ?? null) as LlmProviderDto | null;
      } catch (err) {
        throw err instanceof Error ? err : new Error(pickErrorMessage(err, '创建平台失败'));
      }
    },
    [refresh],
  );

  const updateProvider = useCallback(
    async (id: string, input: UpdateLlmProviderRequest): Promise<LlmProviderDto | null> => {
      const { data, error } = await apiClient.PATCH(
        '/api/v1/ai-model-center/providers/{id}',
        {
          params: { path: { id } },
          body: input,
        },
      );
      if (error) {
        throw new Error(pickErrorMessage(error, '更新平台失败'));
      }
      await refresh();
      return (data ?? null) as LlmProviderDto | null;
    },
    [refresh],
  );

  const deleteProvider = useCallback(
    async (id: string): Promise<boolean> => {
      const { error } = await apiClient.DELETE('/api/v1/ai-model-center/providers/{id}', {
        params: { path: { id } },
      });
      if (error) {
        throw new Error(pickErrorMessage(error, '删除平台失败'));
      }
      await refresh();
      return true;
    },
    [refresh],
  );

  return { ...state, refresh, createProvider, updateProvider, deleteProvider };
}

// ----- Models --------------------------------------------------------------

export function useLlmModels(providerId: string | null): AsyncState<readonly LlmModelDto[]> & {
  refresh: () => Promise<void>;
  upsertModel: (input: UpsertLlmModelRequest) => Promise<LlmModelDto | null>;
} {
  const [state, setState] = useState<AsyncState<readonly LlmModelDto[]>>({
    data: null,
    loading: false,
    error: null,
  });

  const refresh = useCallback(async () => {
    if (!providerId) {
      setState({ data: [], loading: false, error: null });
      return;
    }
    setState({ data: null, loading: true, error: null });
    try {
      const { data, error } = await apiClient.GET('/api/v1/ai-model-center/models', {
        params: { query: { providerId } },
      });
      if (error) {
        setState({ data: null, loading: false, error: pickErrorMessage(error, '加载模型失败') });
        return;
      }
      setState({ data: (data ?? []) as readonly LlmModelDto[], loading: false, error: null });
    } catch (err) {
      setState({ data: null, loading: false, error: pickErrorMessage(err, '加载模型失败') });
    }
  }, [providerId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const upsertModel = useCallback(
    async (input: UpsertLlmModelRequest): Promise<LlmModelDto | null> => {
      const { data, error } = await apiClient.POST('/api/v1/ai-model-center/models', {
        body: input,
      });
      if (error) {
        throw new Error(pickErrorMessage(error, '保存模型失败'));
      }
      await refresh();
      return (data ?? null) as LlmModelDto | null;
    },
    [refresh],
  );

  return { ...state, refresh, upsertModel };
}

// ----- Credentials ---------------------------------------------------------

export function useLlmCredentials(providerId: string | null): AsyncState<readonly LlmCredentialDto[]> & {
  refresh: () => Promise<void>;
  createCredential: (input: CreateLlmCredentialRequest) => Promise<LlmCredentialDto | null>;
  deleteCredential: (credId: string) => Promise<boolean>;
} {
  const [state, setState] = useState<AsyncState<readonly LlmCredentialDto[]>>({
    data: null,
    loading: false,
    error: null,
  });

  const refresh = useCallback(async () => {
    if (!providerId) {
      setState({ data: [], loading: false, error: null });
      return;
    }
    setState({ data: null, loading: true, error: null });
    try {
      const { data, error } = await apiClient.GET(
        '/api/v1/ai-model-center/providers/{id}/credentials',
        {
          params: { path: { id: providerId } },
        },
      );
      if (error) {
        setState({ data: null, loading: false, error: pickErrorMessage(error, '加载凭证失败') });
        return;
      }
      setState({ data: (data ?? []) as readonly LlmCredentialDto[], loading: false, error: null });
    } catch (err) {
      setState({ data: null, loading: false, error: pickErrorMessage(err, '加载凭证失败') });
    }
  }, [providerId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const createCredential = useCallback(
    async (input: CreateLlmCredentialRequest): Promise<LlmCredentialDto | null> => {
      if (!providerId) return null;
      const { data, error } = await apiClient.POST(
        '/api/v1/ai-model-center/providers/{id}/credentials',
        {
          params: { path: { id: providerId } },
          body: input,
        },
      );
      if (error) {
        throw new Error(pickErrorMessage(error, '创建凭证失败'));
      }
      await refresh();
      return (data ?? null) as LlmCredentialDto | null;
    },
    [providerId, refresh],
  );

  const deleteCredential = useCallback(
    async (credId: string): Promise<boolean> => {
      if (!providerId) return false;
      const { error } = await apiClient.DELETE(
        '/api/v1/ai-model-center/providers/{providerId}/credentials/{credId}',
        {
          params: { path: { providerId, credId } },
        },
      );
      if (error) {
        throw new Error(pickErrorMessage(error, '删除凭证失败'));
      }
      await refresh();
      return true;
    },
    [providerId, refresh],
  );

  return { ...state, refresh, createCredential, deleteCredential };
}

// ----- Provider sync -------------------------------------------------------

export async function triggerProviderSync(
  providerId: string,
): Promise<TriggerProviderSyncResponse> {
  const { data, error } = await apiClient.POST(
    '/api/v1/ai-model-center/providers/{id}/sync',
    {
      params: { path: { id: providerId } },
    },
  );
  if (error) {
    throw new Error(pickErrorMessage(error, '同步失败'));
  }
  if (!data) {
    throw new Error('同步返回空响应');
  }
  return data as TriggerProviderSyncResponse;
}

// ----- Call audits ---------------------------------------------------------

export function useLlmCallAudits(filter: { providerId: string | null }): AsyncState<
  readonly LlmCallAuditDto[]
> & {
  refresh: () => Promise<void>;
} {
  const [state, setState] = useState<AsyncState<readonly LlmCallAuditDto[]>>({
    data: null,
    loading: false,
    error: null,
  });

  const refresh = useCallback(async () => {
    setState({ data: null, loading: true, error: null });
    try {
      const { data, error } = await apiClient.GET('/api/v1/ai-model-center/call-audits', {
        params: { query: filter.providerId ? { providerId: filter.providerId } : {} },
      });
      if (error) {
        setState({ data: null, loading: false, error: pickErrorMessage(error, '加载审计失败') });
        return;
      }
      setState({
        data: (data ?? []) as readonly LlmCallAuditDto[],
        loading: false,
        error: null,
      });
    } catch (err) {
      setState({ data: null, loading: false, error: pickErrorMessage(err, '加载审计失败') });
    }
  }, [filter.providerId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { ...state, refresh };
}

export async function fetchUsageSummary(args: {
  providerId?: string;
  sinceDays?: number;
}): Promise<LlmCallAuditAggregateDto | null> {
  const query: { providerId?: string; sinceDays?: number } = {};
  if (args.providerId) query.providerId = args.providerId;
  if (args.sinceDays !== undefined) query.sinceDays = args.sinceDays;
  const { data, error } = await apiClient.GET(
    '/api/v1/ai-model-center/call-audits/usage-summary',
    {
      params: { query },
    },
  );
  if (error) {
    throw new Error(pickErrorMessage(error, '加载用量汇总失败'));
  }
  return (data ?? null) as LlmCallAuditAggregateDto | null;
}
